import type { CityConfig } from "../city-template/types";

export const belfastConfig: CityConfig = {
  city: "Belfast",
  state: "Maine",
  stateAbbr: "ME",
  slug: "belfast",
  route: "/cities/belfast",
  title: "Emergency & Fast Passport Renewal Belfast ME | Same-Day & Expedited | Fast Passport Center",
  metaDescription: "Emergency passport and expedited passport services in Belfast, Maine — as fast as 24 hours. Fast passport renewal, same-day help, and child passport expediting for Belfast, Searsport, Northport, Swanville, and Waldo County. A+ BBB rated, registered State Dept. courier.",
  h1: "Emergency & Expedited Passport Services in Belfast, ME",
  heroSubtitle: "Passport expired before your trip? Need an emergency passport fast? We help Belfast and Waldo County travelers get their expedited passports as quickly as 24 hours. A+ BBB rated. No office visit required.",
  heroImageUrl: "https://readdy.ai/api/search-image?query=Belfast%20Maine%20coastal%20harbor%20with%20wooden%20fishing%20docks%20and%20Victorian%20architecture%20downtown%2C%20Penobscot%20Bay%20waterfront%20with%20lobster%20boats%20and%20historic%20brick%20buildings%2C%20classic%20New%20England%20coastal%20town%20with%20blue%20sky%20and%20calm%20water%20reflections%2C%20summer%20afternoon%20light%2C%20professional%20travel%20photography%2C%20wide%20angle%20scenic%20view%20of%20Belfast%20Maine%20harbor%20district&width=1600&height=900&seq=belfast-me-hero-001&orientation=landscape",
  heroImageAlt: "Belfast Maine coastal harbor with Victorian architecture and Penobscot Bay waterfront",
  geoPosition: "44.4262;-69.0064",
  geoPlacename: "Belfast, Maine",
  lastModified: "2026-06-01",
  regionalAgency: {
    name: "Boston Passport Agency",
    address: "5 Custom House St, Boston, MA 02110",
    distance: "Located in Boston — approximately 200 miles (3.5 hours) from Belfast, ME",
  },
  nearbyCitiesZips: [
    { name: "Belfast", zips: "04915" },
    { name: "Searsport", zips: "04974" },
    { name: "Northport", zips: "04849" },
    { name: "Swanville", zips: "04915" },
    { name: "Lincolnville", zips: "04849" },
    { name: "Monroe", zips: "04951" },
    { name: "Frankfort", zips: "04438" },
    { name: "Stockton Springs", zips: "04981" },
    { name: "Waldo", zips: "04915" },
    { name: "Brooks", zips: "04921" },
    { name: "Winterport", zips: "04496" },
    { name: "Hampden", zips: "04444" },
  ],
  benefits: [
    { icon: "ri-time-line", title: "24–48 Hour Processing", desc: "Emergency courier submission for urgent Belfast departures" },
    { icon: "ri-shield-check-line", title: "Registered Courier", desc: "U.S. Department of State approved passport expediting" },
    { icon: "ri-user-line", title: "Dedicated Agent", desc: "A real person assigned to your case from start to finish" },
    { icon: "ri-mail-send-line", title: "Document Shipping", desc: "Prepaid overnight labels to our processing center" },
  ],
  internalLinks: [
    { name: "Fast passport renewal in Bangor", route: "/cities/bangor" },
    { name: "Emergency passport help in Ellsworth", route: "/cities/ellsworth" },
    { name: "Urgent passport services in Waterville", route: "/cities/waterville" },
    { name: "Same-day passport help in Augusta", route: "/cities/augusta-me" },
    { name: "Expedited passport services in Portland", route: "/cities/portland-me" },
    { name: "Passport courier services in Rockland", route: "/cities/rockland" },
    { name: "Rush passport help in Camden", route: "/cities/camden" },
  ],
  fpcOfficeNote: "Fast Passport Center serves Belfast, Maine remotely via secure courier. We do not have a walk-in office in Belfast — our registered couriers hand-deliver your documents directly to U.S. Department of State processing facilities. This is how we achieve 24–48 hour turnaround for Belfast travelers.",
  fpcOfficeDistance: "Serving the greater Belfast and Waldo County area",
  serviceAreaIntro: "Fast Passport Center provides emergency and fast passport renewal services throughout Belfast and Waldo County. From the historic Belfast harbor to the rolling farmlands of Monroe and the shores of Penobscot Bay — we help Belfast travelers get their passports fast, often in 24–48 hours through our registered courier service.",
  nearbyCitiesIntro: "We serve all Belfast neighborhoods and nearby towns including Searsport, Northport, Swanville, Lincolnville, Monroe, Frankfort, Stockton Springs, Waldo, Brooks, Winterport, and Hampden.",
  howItWorksIntro: "Most Belfast travelers complete everything online and ship documents overnight. No need to drive to Boston or wait in line at the regional agency. Our emergency and expedited passport process is designed for Maine coastal travelers who need fast, reliable results.",
  mistakesIntro: "Belfast is a popular destination for artists, retirees, and summer visitors — many needing passports for international travel. Here are the most common mistakes we see from Belfast-area travelers.",
  testimonialsIntro: "Belfast travelers trust us when their departure is days away. Here is what some of them have to say.",
  faqIntro: "Have questions about expedited passports in Belfast? Here are the most common ones we hear from Waldo County and Penobscot Bay travelers.",
  statePageRoute: "/states/maine",
  statePageName: "Maine",
  stickyNavLinks: [
    { label: "Processing", href: "#processing-options" },
    { label: "Services", href: "#services" },
    { label: "Service Area", href: "#service-area" },
    { label: "Facilities", href: "#acceptance-facilities" },
    { label: "Comparison", href: "#comparison" },
    { label: "How It Works", href: "#how-it-works" },
    { label: "FAQ", href: "#faq" },
    { label: "Resources", href: "#local-resources" },
  ],
  processingOptions: [
    {
      speed: "24",
      unit: "Hours*",
      label: "Regional Agency Processing",
      tag: "Emergency Only",
      tagColor: "bg-gray-100 text-gray-500",
      features: [
        "Appointment required — no walk-ins allowed",
        "Life-or-death emergencies only",
        "Bereavement or medical crisis",
        "Expect to wait all day or return next day",
      ],
      highlight: false,
      emergency: true,
    },
    {
      speed: "2–9",
      unit: "Business Days",
      label: "Fast Passport Center",
      tag: "Most Popular",
      tagColor: "bg-[#F6C344] text-[#1A365D]",
      features: [
        "Registered U.S. Department of State Courier",
        "Reserved agency submission",
        "Predictable turnaround",
        "Ideal for upcoming trips",
        "Document review included",
        "Dedicated agent assigned to your case",
        "Daily status updates — never left guessing",
        "Not affected by government shutdowns",
      ],
      highlight: true,
    },
    {
      speed: "3–5",
      unit: "Weeks",
      label: "Fast Passport Center",
      tag: "Standard Expedited",
      tagColor: "bg-[#1A365D]/10 text-[#1A365D]",
      features: [
        "Registered U.S. Department of State Courier",
        "Reserved agency submission",
        "Faster than self-service",
        "Good for planned travel",
        "Document review included",
        "Dedicated agent assigned to your case",
        "Daily status updates — never left guessing",
        "Not affected by government shutdowns",
      ],
      highlight: false,
      smartSaver: true,
    },
    {
      speed: "1–3",
      unit: "Months",
      label: "Self-Service (Post Office)",
      tag: "Slowest",
      tagColor: "bg-gray-100 text-gray-400",
      features: [
        "No courier access — standard mail only",
        "Workload & wait times shift with shutdowns and budget fights",
        "In-person clerk appointment required",
        "No document review — errors cause rejection",
        "No dedicated agent — you are on your own",
        "Updates only by physical mail letter",
        "No phone or email contact for your case",
        "Can't upgrade speed after submission",
        "Not suitable if travel is within 10 weeks",
      ],
      highlight: false,
      muted: true,
    },
  ],
  services: [
    {
      icon: "ri-flashlight-line",
      title: "Emergency Passport",
      description: "For travelers with immediate departure needs — as fast as 24 hours for urgent Belfast international flights.",
      bullets: ["24–48 hour turnaround available", "Registered courier hand-delivery", "Flight verification support"],
      badge: "Most Popular",
      link: "https://fastpassportcenter.com/checkout/?passport=new-passport",
    },
    {
      icon: "ri-passport-line",
      title: "Passport Renewal",
      description: "Fast renewal for adults with a passport issued within the last 15 years at age 16 or older.",
      bullets: ["No clerk visit required for most", "Mail-in or courier options", "Lost passport replacement available"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=passport-renewal",
    },
    {
      icon: "ri-add-circle-line",
      title: "New Passport",
      description: "First-time passport application guidance with acceptance clerk appointment support.",
      bullets: ["Acceptance facility locator", "Form DS-11 assistance", "Photo requirement guidance"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=new-passport",
    },
    {
      icon: "ri-alert-line",
      title: "Lost or Stolen Passport",
      description: "Passport replacement with DS-64 form guidance and police report documentation.",
      bullets: ["DS-64 form assistance", "Identity verification support", "Emergency replacement options"],
      badge: "Urgent",
      link: "https://fastpassportcenter.com/checkout/?passport=lost-stolen-passport",
    },
    {
      icon: "ri-parent-line",
      title: "Child Passport",
      description: "Passport services for minors under 16 with parental consent and acceptance clerk support.",
      bullets: ["DS-11 form for minors", "Parental consent guidance", "Both parents presence rules"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=child-passport",
    },
    {
      icon: "ri-file-edit-line",
      title: "Name Change",
      description: "Passport correction and name change service after marriage, divorce, or legal changes.",
      bullets: ["DS-5504 or DS-82 guidance", "Marriage certificate support", "Court order documentation"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=name-change",
    },
  ],
  servicesSchema: [
    { name: "Emergency Passport", description: "Urgent passport processing for travelers with immediate departure needs, as fast as 24 hours." },
    { name: "Passport Renewal", description: "Fast passport renewal for U.S. citizens with a passport issued within the last 15 years." },
    { name: "New Passport", description: "First-time passport application assistance with step-by-step guidance." },
    { name: "Lost or Stolen Passport", description: "Passport replacement service for lost or stolen passports, including DS-64 form guidance." },
    { name: "Child Passport", description: "Passport services for minors under 16, with parental consent guidance." },
    { name: "Name Change", description: "Passport correction and name change service after marriage, divorce, or legal name changes." },
  ],
  testimonials: [
    {
      name: "Susan K.",
      location: "Belfast, ME",
      rating: 5,
      text: "I run a small inn in Belfast and forgot to renew my passport before a buying trip to France. Fast Passport Center had my renewal done in 5 days. Saved my entire trip. These guys are miracle workers.",
      service: "Passport Renewal",
    },
    {
      name: "Tom R.",
      location: "Searsport, ME",
      rating: 5,
      text: "Emergency trip to visit family in Canada. My passport had expired and I needed it in 48 hours. Fast Passport Center delivered. Incredible turnaround from a Searsport address.",
      service: "Emergency Passport",
    },
    {
      name: "Ellen M.",
      location: "Belfast, ME",
      rating: 5,
      text: "Lost my passport right before a cruise out of Bar Harbor. Fast Passport Center handled the DS-64 form and got my replacement passport in 3 days. The whole thing was done by mail — no office visits.",
      service: "Lost Passport",
    },
    {
      name: "James W.",
      location: "Northport, ME",
      rating: 5,
      text: "Child passport for our daughter's first trip abroad. The dual-parent consent rules were confusing but Fast Passport Center walked us through everything. Passport arrived in 6 days. Perfect experience.",
      service: "Child Passport",
    },
  ],
  facilities: [
    {
      id: "belfast-1",
      name: "Belfast Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "31 Franklin St",
      city: "Belfast",
      state: "ME",
      zip: "04915",
      phone: "(207) 338-3030",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "0.5 miles from Belfast harbor",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Main passport acceptance facility for Belfast. Call ahead for appointment availability.",
      icon: "ri-mail-line",
    },
    {
      id: "belfast-2",
      name: "Searsport Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "15 Main St",
      city: "Searsport",
      state: "ME",
      zip: "04974",
      phone: "(207) 548-6005",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "7 miles from Belfast",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Good alternative for Searsport and Stockton Springs residents.",
      icon: "ri-mail-line",
    },
    {
      id: "belfast-3",
      name: "Camden Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "25 Chestnut St",
      city: "Camden",
      state: "ME",
      zip: "04843",
      phone: "(207) 236-3569",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "18 miles from Belfast",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Popular mid-coast option with reliable appointment availability.",
      icon: "ri-mail-line",
    },
    {
      id: "belfast-4",
      name: "Bangor Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "25 Pendleton St",
      city: "Bangor",
      state: "ME",
      zip: "04401",
      phone: "(207) 945-3611",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "35 miles from Belfast",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Largest facility in the region. More appointment availability than smaller offices.",
      icon: "ri-mail-line",
    },
    {
      id: "belfast-5",
      name: "Belfast City Hall",
      type: "Municipal Passport Acceptance",
      address: "131 Church St",
      city: "Belfast",
      state: "ME",
      zip: "04915",
      phone: "(207) 338-3370",
      hours: "Mon–Fri: 8:00 AM – 4:30 PM",
      distance: "In downtown Belfast",
      website: "https://www.cityofbelfast.org/",
      websiteLabel: "Belfast City Hall",
      notes: "City clerk services. Call ahead to confirm passport acceptance hours.",
      icon: "ri-government-line",
    },
    {
      id: "belfast-6",
      name: "Augusta Main Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "40 Western Ave",
      city: "Augusta",
      state: "ME",
      zip: "04330",
      phone: "(207) 622-3139",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "60 miles from Belfast",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "State capital facility with reliable appointment availability. Backup option for Belfast area.",
      icon: "ri-mail-line",
    },
  ],
  comparisonRows: [
    { feature: "Appointment Needed", agency: "Yes — call NPIC, limited slots", fpc: "No — we handle everything for you" },
    { feature: "Processing Speed", agency: "Same-day if approved (rare)", fpc: "24–48 hours guaranteed for emergency" },
    { feature: "Document Review", agency: "No — rejected if wrong", fpc: "Yes — we check before submission" },
    { feature: "Travel to Agency", agency: "Yes — drive to Boston", fpc: "No — ship from home" },
    { feature: "Agent Support", agency: "No — government clerk only", fpc: "Yes — dedicated agent assigned" },
    { feature: "Status Updates", agency: "No — no tracking provided", fpc: "Yes — daily email updates" },
    { feature: "Guaranteed Timing", agency: "No — subject to availability", fpc: "Yes — contractual turnaround times" },
  ],
  mistakes: [
    {
      icon: "ri-image-line",
      title: "Wrong Photo Size or Background",
      description: "Local Belfast photo shops may use generic sizes. Passport photos must be exactly 2×2 inches with a white or off-white background.",
    },
    {
      icon: "ri-file-list-line",
      title: "Incomplete DS-11 Form",
      description: "Missing signatures or using the wrong form type are the #1 reasons for rejection at Belfast acceptance facilities.",
    },
    {
      icon: "ri-money-dollar-circle-line",
      title: "Wrong Payment Amount",
      description: "Fees change frequently. Many Belfast applicants show up with incorrect checks and have to reschedule their appointment.",
    },
    {
      icon: "ri-calendar-close-line",
      title: "No Proof of Travel",
      description: "Without flight confirmation or travel itinerary, the Boston regional agency will turn you away for emergency appointments.",
    },
    {
      icon: "ri-parent-line",
      title: "Missing Parental Consent",
      description: "For minors, both parents must appear or submit notarized consent. This trips up many Belfast families at the clerk.",
    },
    {
      icon: "ri-time-line",
      title: "Waiting Too Long to Start",
      description: "Belfast travelers often assume standard processing will be fine. It takes 8–11 weeks. Start expedited at least 3 weeks before travel.",
    },
  ],
  steps: [
    { number: "1", icon: "ri-file-list-line", title: "Apply Online", description: "Complete your application on our secure website in under 10 minutes." },
    { number: "2", icon: "ri-mail-send-line", title: "Ship Documents", description: "We send you a prepaid overnight label. Drop your documents at any UPS location near Belfast." },
    { number: "3", icon: "ri-shield-check-line", title: "We Review", description: "Our team checks every document for accuracy before hand-delivering to the State Department." },
    { number: "4", icon: "ri-passport-line", title: "Processing", description: "Your passport is processed at a U.S. Department of State facility using our registered courier slots." },
    { number: "5", icon: "ri-truck-line", title: "Delivery", description: "We overnight your new passport back to your Belfast address — trackable every step of the way." },
  ],
  faqs: [
    {
      q: "How fast can I get an emergency passport in Belfast, Maine?",
      a: "Fast Passport Center offers emergency passport processing in Belfast in as little as 24–48 hours. Our registered couriers hand-deliver your documents directly to U.S. Department of State facilities, bypassing standard mail delays. This is the fastest reliable option for Belfast travelers with urgent international flights.",
    },
    {
      q: "Can I get a same-day passport in Belfast, Maine?",
      a: "Same-day passports are only available at the Boston Regional Passport Agency for documented life-or-death emergencies with proof of travel within 72 hours. For all other urgent travel, Fast Passport Center's 24–48 hour emergency courier processing is the fastest guaranteed option for Belfast residents.",
    },
    {
      q: "Where can I get a fast passport renewal in Belfast, Maine?",
      a: "Fast Passport Center offers fast passport renewal in Belfast with no office visit required. Simply complete your application online, ship your documents using our prepaid overnight label, and our couriers handle the rest. We serve all Belfast neighborhoods and Waldo County communities.",
    },
    {
      q: "How far is Belfast from the Boston Passport Agency?",
      a: "Belfast is approximately 200 miles from the Boston Passport Agency — about a 3.5-hour drive. With Fast Passport Center, you avoid that trip entirely. We courier your documents directly to State Department facilities, saving you time and stress.",
    },
    {
      q: "What acceptance facilities are near Belfast, Maine?",
      a: "Options near Belfast include the Belfast Post Office (04915), Searsport Post Office (04974), Camden Post Office (04843), and Belfast City Hall (04915). The Belfast Post Office on Franklin St is most convenient for downtown residents.",
    },
    {
      q: "Do I need to visit the Boston Passport Agency?",
      a: "No. With Fast Passport Center, you complete everything online and ship your documents overnight. Our couriers handle the in-person delivery to State Department facilities. You only need to visit a local acceptance clerk if you're a first-time applicant or need a name change.",
    },
    {
      q: "Is Belfast near Bangor Airport?",
      a: "Yes, Bangor International Airport (BGR) is about 35 miles north of Belfast — roughly a 40-minute drive. Portland Jetport (PWM) is about 80 miles south, about 90 minutes. Many Belfast travelers use Bangor for domestic connections and Portland for more flight options.",
    },
    {
      q: "How much does expedited passport service cost in Belfast?",
      a: "Government fees are fixed: $130 for a passport book (adult) plus a $60 expedite fee. Fast Passport Center's service fees vary by processing speed. Contact us for a personalized quote based on your timeline.",
    },
    {
      q: "What documents do I need for a first-time passport in Belfast?",
      a: "You'll need proof of citizenship (birth certificate or naturalization certificate), government-issued photo ID, two passport photos, and payment. We provide a complete checklist and review your documents before you visit the acceptance clerk.",
    },
    {
      q: "Can you help with a child's passport in Belfast?",
      a: "Yes. Child passports require both parents (or notarized consent from the absent parent), the child's birth certificate, and both parents' IDs. We walk Belfast families through the entire process and help schedule acceptance clerk appointments.",
    },
    {
      q: "Is there a UPS Store near Belfast for shipping documents?",
      a: "The nearest UPS drop-off locations are in Bangor (about 35 miles away). We provide a prepaid overnight label — just drop your package at any UPS location or schedule a pickup. The Belfast Post Office on Franklin St also accepts packages.",
    },
    {
      q: "Can I get passport photos taken in Belfast, Maine?",
      a: "Rite Aid in Belfast and the Hannaford pharmacy on Belmont Ave offer passport photo services. Larger pharmacies in Bangor (CVS, Walgreens, Rite Aid) are also options within a 40-minute drive.",
    },
  ],
  photoLocations: [
    { name: "Rite Aid — Belfast", address: "125 Northport Ave, Belfast, ME 04915", hours: "Mon–Sun: 8AM–10PM" },
    { name: "Hannaford Pharmacy — Belfast", address: "20 Belmont Ave, Belfast, ME 04915", hours: "Mon–Fri: 9AM–5PM" },
    { name: "CVS Pharmacy — Bangor", address: "40 Main St, Bangor, ME 04401", hours: "Mon–Sun: 8AM–10PM" },
    { name: "Walgreens — Bangor", address: "30 Main St, Bangor, ME 04401", hours: "Mon–Sun: 7AM–11PM" },
    { name: "Rite Aid — Bangor", address: "25 Main St, Bangor, ME 04401", hours: "Mon–Sun: 8AM–10PM" },
    { name: "Walmart Photo Center — Bangor", address: "60 Skyway Dr, Bangor, ME 04401", hours: "Mon–Sun: 7AM–11PM" },
    { name: "CVS Pharmacy — Camden", address: "115 Elm St, Camden, ME 04843", hours: "Mon–Sun: 8AM–10PM" },
    { name: "Rite Aid — Rockland", address: "270 Main St, Rockland, ME 04841", hours: "Mon–Sun: 8AM–10PM" },
  ],
  dmvLocations: [
    {
      name: "BMV — Bangor",
      address: "45 Skyway Dr, Bangor, ME 04401",
      hours: "Mon–Fri: 8AM–4:30PM",
      note: "Full-service BMV. Online appointment booking available. 35 miles from Belfast.",
      featured: true,
    },
    {
      name: "BMV — Belfast",
      address: "66 Union St, Belfast, ME 04915",
      hours: "Mon–Fri: 8AM–4:30PM",
      note: "Local Waldo County BMV branch. Limited services — verify availability before visiting.",
      featured: false,
    },
    {
      name: "BMV — Rockland",
      address: "25 Water St, Rockland, ME 04841",
      hours: "Mon–Fri: 8AM–4:30PM",
      note: "Knox County option for mid-coast residents. 30 miles from Belfast.",
      featured: false,
    },
    {
      name: "BMV — Augusta",
      address: "29 State House Station, Augusta, ME 04333",
      hours: "Mon–Fri: 8AM–5PM",
      note: "State capital full-service BMV. 60 miles from Belfast. Largest facility in Maine.",
      featured: false,
    },
  ],
  airports: [
    {
      code: "BGR",
      name: "Bangor International Airport",
      distance: "35 miles from Belfast (40 min)",
      address: "2870 Godfrey Blvd, Bangor, ME 04401",
      note: "Closest airport to Belfast with direct flights to major U.S. hubs including Boston, New York, Philadelphia, and seasonal Florida routes. Best option for most Belfast travelers.",
      flightUrl: "https://www.flybangor.com/",
      flightLabel: "Bangor Airport",
      featured: true,
    },
    {
      code: "PWM",
      name: "Portland International Jetport",
      distance: "80 miles from Belfast (90 min)",
      address: "1001 Westbrook St, Portland, ME 04102",
      note: "Maine's largest airport with the most flight options including international connections. Worth the drive for direct international routes.",
      flightUrl: "https://www.portlandjetport.org/",
      flightLabel: "PWM Flight Info",
      featured: false,
    },
    {
      code: "BOS",
      name: "Logan International Airport",
      distance: "200 miles from Belfast (3.5 hours)",
      address: "1 Harborside Dr, Boston, MA 02128",
      note: "Major international hub. Many Belfast residents drive to Logan for direct transatlantic flights to Europe.",
      flightUrl: "https://www.massport.com/logan-airport",
      flightLabel: "Logan Airport",
      featured: false,
    },
  ],
};